import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  OnInit,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { select, Store } from '@ngxs/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';
import { WooCommerceCoupon } from '../../../../../core/interfaces/woo-commerce/api/coupon/coupon-api.interface';
import { UserProfileApiService } from '../../../../../core/services/user-profile/user-profile-api.service';
import { FormattedCurrencyPipe } from '../../../../../shared/pipes/formatted-currency.pipe';
import { ButtonComponent } from '../../../../../shared/components/button/button.component';
import {
  InputComponent,
  InputStyle
} from '../../../../../shared/components/controllers/input/input.component';
import { ClickableRowComponent } from '../../../../../shared/components/clickable-row/clickable-row.component';
import { slideInOut } from '../../../../../shared/transitions/slide-in-out.transition';
import { ButtonStyle } from '../../../../../shared/enums/button.enum';
import { AuthState } from '../../../../../shared/state/auth/auth.state';
import { CartState } from '../../../../../shared/state/cart/cart.state';
import {
  CartCouponAction,
  CartItemsAction,
  CartRewardPointsAction
} from '../../../../../shared/state/cart/cart.actions';

@UntilDestroy()
@Component({
  selector: 'app-checkout-details-coupon',
  imports: [
    FormattedCurrencyPipe,
    ButtonComponent,
    InputComponent,
    FormsModule,
    ClickableRowComponent
  ],
  animations: [slideInOut],
  templateUrl: './checkout-details-coupon.component.html',
  styleUrl: './checkout-details-coupon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FormattedCurrencyPipe]
})
export class CheckoutDetailsCouponComponent implements OnInit {
  isLocked: InputSignal<boolean> = input<boolean>(false);
  couponControl: WritableSignal<string> = signal<string>('');
  rewardPointsControl: WritableSignal<string> = signal<string>('');
  isCouponExpanded: WritableSignal<boolean> = signal<boolean>(false);

  coupons: Signal<WooCommerceCoupon[]> = select(CartState.coupons);
  totals = select(CartState.totals);
  isCouponLoading: Signal<boolean> = select(CartState.isLoading('addCoupon'));
  isRewardPointsLoading: Signal<boolean> = select(
    CartState.isLoading('rewardPoints')
  );
  isAuthenticated: Signal<boolean> = select(AuthState.isAuthenticated);
  availableRewardPoints: WritableSignal<number> = signal<number>(0);
  rewardPointsWorth: WritableSignal<number | null> = signal<number | null>(null);
  appliedRewardPoints = computed(() => Number(this.totals()?.points_redeemed || 0));
  appliedRewardDiscount = computed(() => this.totals()?.points_discount || '0');
  rewardMinorUnit = computed(() => this.totals()?.currency_minor_unit || 2);

  protected readonly InputStyle = InputStyle;
  protected readonly ButtonStyle = ButtonStyle;

  private readonly userProfileApiService = inject(UserProfileApiService);

  constructor(
    private store: Store,
    private toastrService: ToastrService,
    private formattedCurrencyPipe: FormattedCurrencyPipe
  ) {}

  ngOnInit() {
    this.getCoupons();
    this.rewardPointsControl.set(
      this.appliedRewardPoints() > 0 ? String(this.appliedRewardPoints()) : ''
    );

    if (this.isAuthenticated()) {
      this.loadRewardPointsBalance();
    }
  }

  onCouponCheckClick(coupon: string = this.couponControl()) {
    if (this.isLocked()) {
      return;
    }

    const normalizedCoupon = coupon.trim();
    if (!normalizedCoupon) {
      return;
    }

    this.store
      .dispatch(new CartCouponAction.Add(normalizedCoupon))
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          const coupon: WooCommerceCoupon = this.coupons()[0];
          const totalDiscount = this.formattedCurrencyPipe.transform(
            coupon.totals.total_discount,
            coupon.totals.currency_minor_unit
          );

          this.toastrService.success(
            `Your Total Discount - ${totalDiscount}`,
            'Coupon added!'
          );

          this.store.dispatch(new CartItemsAction.Get());
          this.couponControl.set(coupon?.code || normalizedCoupon);
        },
        error: (error: { error?: { message?: string }; message?: string }) => {
          this.toastrService.error(
            error?.error?.message || error?.message || 'Failed to add coupon.',
            'Coupon not applied'
          );
        }
      });
  }

  onRewardPointsRedeemClick(): void {
    if (this.isLocked() || !this.isAuthenticated()) {
      return;
    }

    const parsedRewardPoints = this.parseRewardPointsInput();
    if (parsedRewardPoints == null) {
      return;
    }

    this.store
      .dispatch(new CartRewardPointsAction.Set(parsedRewardPoints))
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          const appliedRewardDiscount = this.formattedCurrencyPipe.transform(
            this.appliedRewardDiscount(),
            this.rewardMinorUnit()
          );

          this.rewardPointsControl.set(
            this.appliedRewardPoints() > 0 ? String(this.appliedRewardPoints()) : ''
          );

          this.toastrService.success(
            `Applied ${this.appliedRewardPoints()} points for ${appliedRewardDiscount} off.`,
            'Reward points updated'
          );
        },
        error: (error: { error?: { message?: string }; message?: string }) => {
          this.toastrService.error(
            error?.error?.message ||
              error?.message ||
              'Failed to redeem reward points.',
            'Reward points not applied'
          );
        }
      });
  }

  onRewardPointsRemoveClick(): void {
    if (this.isLocked() || this.isRewardPointsLoading()) {
      return;
    }

    this.store
      .dispatch(new CartRewardPointsAction.Remove())
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.rewardPointsControl.set('');
          this.toastrService.success(
            'Reward points were removed from your cart.',
            'Reward points removed'
          );
        },
        error: (error: { error?: { message?: string }; message?: string }) => {
          this.toastrService.error(
            error?.error?.message ||
              error?.message ||
              'Failed to remove reward points.',
            'Reward points not removed'
          );
        }
      });
  }

  private getCoupons() {
    this.store
      .dispatch(new CartCouponAction.Get())
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          const coupon: WooCommerceCoupon = this.coupons()[0];
          this.couponControl.set(coupon?.code || '');
        }
      });
  }

  private loadRewardPointsBalance(): void {
    this.userProfileApiService
      .getPoints()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          this.availableRewardPoints.set(response.data?.available_balance ?? 0);
          this.rewardPointsWorth.set(response.data?.worth ?? null);
        },
        error: () => {
          this.availableRewardPoints.set(0);
          this.rewardPointsWorth.set(null);
        }
      });
  }

  private parseRewardPointsInput(): number | null {
    const normalizedValue = this.rewardPointsControl().trim();
    const parsedRewardPoints = Number.parseInt(normalizedValue, 10);

    if (!normalizedValue || Number.isNaN(parsedRewardPoints) || parsedRewardPoints <= 0) {
      this.toastrService.error(
        'Enter a valid positive number of reward points.',
        'Invalid reward points'
      );
      return null;
    }

    if (parsedRewardPoints > this.availableRewardPoints()) {
      this.toastrService.error(
        `You can redeem up to ${this.availableRewardPoints()} points.`,
        'Invalid reward points'
      );
      return null;
    }

    return parsedRewardPoints;
  }
}
